import { AxiosError } from "axios";
import { toast } from "react-hot-toast";
import { AuthenticatedBaseService } from "@anythink-cloud/sdk";
import { getAuthClient } from "@/lib/auth";
import { config } from "@/config/env";

const authClient = getAuthClient();

// Helper function to extract org ID from current URL
const getCurrentOrgId = (): string | null => {
  if (typeof window === "undefined") return null;

  const pathname = window.location.pathname;
  const match = pathname.match(/^\/org\/(\d+)/);
  return match ? match[1] : null;
};

/**
 * BaseService class that extends the SDK BaseService
 * Adds tenant ID replacement, toast notifications, and static methods for backward compatibility
 */
export class BaseService extends AuthenticatedBaseService {
  constructor() {
    super(authClient, config.instanceUrl);

    // Add tenant id interceptor
    this.client.interceptors.request.use((config) => {
      const token = authClient.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const currentOrgId = getCurrentOrgId();
      if (config.url && config.url.includes("/<tenant_id>/") && currentOrgId) {
        config.url = config.url.replace("/<tenant_id>/", `/${currentOrgId}/`);
      }

      return config;
    });
  }

  /**
   * Process URL to replace tenant ID placeholder
   */
  protected processUrl(url: string, requiresTenantId: boolean): string {
    if (requiresTenantId) {
      url = "/org/<tenant_id>" + url;
    }
    return url;
  }

  /**
   * GET request with tenant ID support (instance method)
   */
  protected async getRequest<T>(
    url: string,
    requiresTenantId: boolean = true,
  ): Promise<T> {
    try {
      url = this.processUrl(url, requiresTenantId);
      return super.get<T>(url);
    } catch (error) {
      BaseService.handleError(error as AxiosError);
      throw error;
    }
  }

  /**
   * POST request with tenant ID support and toast notifications (instance method)
   */
  protected async postRequest<TRequest, TResponse>(
    url: string,
    data: TRequest,
    successMessage?: string,
    requiresTenantId: boolean = true,
    suppressSuccessMessage: boolean = false,
    suppressErrorToast: boolean = false,
  ): Promise<TResponse> {
    try {
      url = this.processUrl(url, requiresTenantId);
      const response = await super.post<TRequest, TResponse>(url, data);
      if (!suppressSuccessMessage) {
        toast.success(successMessage || "Submitted successfully");
      }
      return response;
    } catch (error) {
      if (!suppressErrorToast) BaseService.handleError(error as AxiosError);
      throw error;
    }
  }

  /**
   * POST request with form data (instance method)
   */
  protected async postFormDataRequest<FormData, TResponse>(
    url: string,
    data: FormData,
    successMessage?: string,
    requiresTenantId: boolean = true,
  ): Promise<TResponse> {
    try {
      url = this.processUrl(url, requiresTenantId);
      const response = await super.postFormData<FormData, TResponse>(url, data);
      toast.success(successMessage || "Submitted successfully");
      return response;
    } catch (error) {
      BaseService.handleError(error as AxiosError);
      throw error;
    }
  }

  /**
   * PUT request with tenant ID support and toast notifications (instance method)
   */
  protected async putRequest<TRequest, TResponse>(
    url: string,
    data: TRequest,
    successMessage?: string,
    requiresTenantId: boolean = true,
    suppressSuccessMessage: boolean = false,
  ): Promise<TResponse> {
    try {
      url = this.processUrl(url, requiresTenantId);
      const response = await super.put<TRequest, TResponse>(url, data);
      if (!suppressSuccessMessage) {
        toast.success(successMessage || "Submitted successfully");
      }
      return response;
    } catch (error) {
      BaseService.handleError(error as AxiosError);
      throw error;
    }
  }

  /**
   * DELETE request with tenant ID support and toast notifications (instance method)
   */
  protected async deleteRequest(
    url: string,
    successMessage?: string,
    requiresTenantId: boolean = true,
    suppressSuccessMessage: boolean = false,
  ): Promise<void> {
    try {
      url = this.processUrl(url, requiresTenantId);
      await super.delete(url);
      if (!suppressSuccessMessage) {
        toast.success(successMessage || "Successfully deleted");
      }
    } catch (error) {
      BaseService.handleError(error as AxiosError);
      throw error;
    }
  }

  /**
   * Handle errors with toast notifications
   */
  private static handleError(error: AxiosError): void {
    // Don't show error toast for auth errors as they will be handled by the interceptor
    if (error.response?.status === 401) {
      return;
    }

    // Don't show error toast for 404 errors as they will be handled by the component
    if (error.response?.status === 404) {
      return;
    }

    let message = "An error occurred";
    if (error.response?.data) {
      const data = error.response.data as any;
      if (typeof data === "string") {
        message = data;
      } else if (typeof data === "object" && data !== null) {
        message = data.error || data.message || JSON.stringify(data);
      }
    } else if (error.message) {
      message = error.message;
    }
    toast.error(String(message));
  }
}
